import type { ColumnDef } from '@tanstack/react-table';

/**
 * A DataTable's search box and facet filters, kept in the URL so a filtered
 * list survives a refresh and can be shared. Each table on a page gets its own
 * prefix: `<prefix>_q` carries the search text and `<prefix>_f_<column>` is
 * repeated once per selected facet value.
 */
export interface ColumnFilter {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFacetColumnIds(columns: ColumnDef<any>[]) {
  return columns
    .map((col) => {
      const meta = (col as { meta?: { facet?: unknown } }).meta;
      if (!meta?.facet) return null;
      if ('id' in col && col.id) return String(col.id);
      if ('accessorKey' in col && typeof col.accessorKey === 'string') return col.accessorKey;
      return null;
    })
    .filter(Boolean) as string[];
}

/** Whether the URL carries any filter for this table at all. */
export function hasTableFiltersInUrl({ searchParams, prefix, facetColumnIds }: {
  searchParams: URLSearchParams;
  prefix: string;
  facetColumnIds: string[];
}): boolean {
  if (searchParams.has(`${prefix}_q`)) return true;
  return facetColumnIds.some((colId) => searchParams.has(`${prefix}_f_${colId}`));
}

export function decodeTableFilters({ searchParams, prefix, facetColumnIds, searchColumnId }: {
  searchParams: URLSearchParams;
  prefix: string;
  facetColumnIds: string[];
  searchColumnId: string;
}): ColumnFilter[] {
  const filters: ColumnFilter[] = [];

  if (searchColumnId) {
    const q = searchParams.get(`${prefix}_q`);
    if (q != null && q !== '') {
      filters.push({ id: searchColumnId, value: q });
    }
  }

  for (const colId of facetColumnIds) {
    const values = searchParams.getAll(`${prefix}_f_${colId}`);
    if (values.length > 0) {
      filters.push({ id: colId, value: values });
    }
  }

  return filters;
}

export function encodeTableFilters({ searchParams, prefix, facetColumnIds, searchColumnId, filters }: {
  searchParams: URLSearchParams;
  prefix: string;
  facetColumnIds: string[];
  searchColumnId: string;
  filters: ColumnFilter[];
}) {
  const next = new URLSearchParams(searchParams);

  // clear existing keys for this table
  next.delete(`${prefix}_q`);
  for (const colId of facetColumnIds) {
    next.delete(`${prefix}_f_${colId}`);
  }

  // write search
  if (searchColumnId) {
    const q = filters.find((f) => f.id === searchColumnId)?.value;
    if (typeof q === 'string' && q.trim() !== '') {
      next.set(`${prefix}_q`, q);
    }
  }

  // write facets (multi values)
  for (const colId of facetColumnIds) {
    const value = filters.find((f) => f.id === colId)?.value;
    const values = Array.isArray(value) ? value : value ? [value] : [];
    for (const v of values) {
      if (v != null && String(v) !== '') next.append(`${prefix}_f_${colId}`, String(v));
    }
  }

  return next;
}
